import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import './Navbar.css'

import categories from '../../data/categories';
import SideMenu from '../SideMenu/SideMenu';

function Navbar () {
    const navigate = useNavigate();

    // ref관리
    const searchBarRef = useRef(null);
    const searchIconRef = useRef(null);
    const loginPromptRef = useRef(null);

    // 로그인 상태, 검색창, 메뉴 state
    let isLoggedIn = useSelector((state) => state.isLoggedIn);
    let [isSearchOpen, setIsSearchOpen] = useState(false);
    let [isLoginPromptOpen, setIsLoginPromptOpen] = useState(false);
    let [isSideMenuOpen, setIsSideMenuOpen] = useState(false);

    // 드롭다운 활성화 상태 관리
    const [activeDropdown, setActiveDropdown] = useState(null);

    // 마우스가 navbar와 드롭다운 영역을 벗어났을 때만 드롭다운 닫기
    const handleMouseLeave = (event) => {
        // const isInNavOrDropdown = event.relatedTarget?.closest('.dropdown');
        // if (!isInNavOrDropdown) {
        //     setActiveDropdown(null);
        // }
        setActiveDropdown(null);
    };
    
    // 검색창 토글 함수
    const toggleSearch = useCallback(() => {
        setIsSearchOpen((prev) => !prev);
    }, []);

    const toggleLoginPrompt = useCallback(() => {
        setIsLoginPromptOpen((prev) => !prev)
    }, [])

    // 사이드메뉴 토글 함수
    const toggleSideMenu = () => {
        setIsSideMenuOpen((prev) => !prev);
    };

    // 링크 태그 경로 설정
    const handleLinkClick = (e, target) => {
        e.preventDefault(); // 기본 링크 동작 방지
        e.stopPropagation(); // 이벤트 전파 중단 (외부 클릭 처리 방지)

        if (!isLoggedIn) {
            setIsLoginPromptOpen(true); // 로그인 프롬프트 열기
        } else {
            navigate(target); // 로그인 상태라면 이동
        }
    };

    // 검색창 외부 클릭 시 닫기
    useEffect(() => {
        if (isSearchOpen) {
            const handleOutsideClick = (event) => {
                const refs = [searchBarRef.current, searchIconRef.current];
                const isInsideClick = refs.some((ref) => ref && ref.contains(event.target));

                // 카트 또는 마이페이지 링크 클릭 시 검색창 닫기 방지
                const isLinkClick = event.target.closest('.navbar__cart-icons');

                if (!isInsideClick && !isLinkClick) {
                    toggleSearch();
                }
            };

            document.addEventListener('click', handleOutsideClick);

            return () => {
                document.removeEventListener('click', handleOutsideClick);
            };
        }
    }, [isSearchOpen, toggleSearch]);


    // 로그인 프롬프트 외부 클릭 시 닫기
    useEffect(() => {
        if (isLoginPromptOpen) {
            const handleOutsideClick = (event) => {
                const refs = [loginPromptRef.current];
                const isInsideClick = refs.some((ref) => ref && ref.contains(event.target));

                if (!isInsideClick) {
                    setIsLoginPromptOpen(false); // 로그인 프롬프트 닫기
                }
            };

            document.addEventListener('click', handleOutsideClick);

            return () => {
                document.removeEventListener('click', handleOutsideClick);
            };
        }
    }, [isLoginPromptOpen]);

    return (
        <>
            <nav className = "navbar common-padding" onMouseLeave={handleMouseLeave}>
                <div className="navbar-content">
                    {/* 로고 */}
                    <div className="logo">
                        <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                            <img src={`${process.env.PUBLIC_URL}/Logo_spell.png`} alt="Pick & Sell Logo" />
                        </Link>
                    </div>

                    {/* 카테고리 */}
                    <div className="navbar-categories">
                        {categories.map((category, index) => (
                            <div
                                className="category-item"
                                key={index}
                                onMouseEnter={() => setActiveDropdown(index)}
                            >
                                <Link to="/shop" className="category-link">
                                    {category.name.toLowerCase()}
                                </Link>
                            </div>
                        ))}
                    </div>

                    {/* 오른쪽 링크 및 검색창 */}
                    <div className="navbar-icons">
                        <div ref={searchBarRef} className={`search-bar ${isSearchOpen ? 'search-bar--open' : ''}`}>
                            <input type="text" placeholder="검색어를 입력해주세요" />
                            <button className="search-btn">
                                <img src={`${process.env.PUBLIC_URL}/search.png`} ref={searchIconRef} alt="Search Icon" className="navbar-icon" />
                            </button>
                        </div>
                        <div ref={searchIconRef} className="navbar-icon-wrapper navbar__search-icons" onClick={toggleSearch}>
                            <img src={`${process.env.PUBLIC_URL}/search.png`} alt="Search Icon" className="navbar-icon" />
                        </div>
                        <Link to="/cart" className="navbar-icon-wrapper navbar__cart-icons">
                            <img src={`${process.env.PUBLIC_URL}/cart.png`} alt="Cart Icon" className="navbar-icon" onClick={(e) => handleLinkClick(e, '/cart')} />
                        </Link>
                        <Link to="/mypage" className="navbar-icon-wrapper navbar__cart-icons">
                            <img src={`${process.env.PUBLIC_URL}/my.png`} alt="My Icon" className="navbar-icon" onClick={(e) => handleLinkClick(e, '/mypage')} />
                        </Link>
                    </div>

                    <button className="navbar__hamburger" onClick={toggleSideMenu} variant="primary">
                        ☰
                    </button>

                    {/* {login Prompt} */}
                    <div className={`login-prompt ${isLoginPromptOpen ? 'login-prompt--open' : ''}`} ref={loginPromptRef} >
                        <h2>로그인이 필요합니다.</h2>
                        <button onClick={() => {
                            navigate('/login')
                            toggleLoginPrompt();
                        }}>로그인</button>
                    </div>
                </div>
        </nav >

        {/* Dropdown (Navbar 외부) */ }
        {
            categories.map((category, index) => (
                <div
                    className={`dropdown ${activeDropdown === index ? 'visible' : ''}`} // 활성화 상태 확인
                    key={index}
                    onMouseEnter={() => setActiveDropdown(index)} // 드롭다운 위에 마우스가 있으면 상태 유지
                    onMouseLeave={handleMouseLeave}
                >
                    <div className="dropdown-wrapper">
                        {category.subcategories.map((sub, idx) => (
                            <Link to="/shop" className="dropdown-item" key={idx}>
                                {sub}
                            </Link>
                        ))}
                    </div>
                </div>
            ))
        }

        <SideMenu isSideMenuOpen={isSideMenuOpen} toggleSideMenu={toggleSideMenu}></SideMenu>
    </>
    )
}

export default Navbar;